import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Badge, Spinner } from 'react-bootstrap';
import axios from 'axios';
import config from '../../config';
import { toast } from 'react-toastify';
import { FiSave, FiPlus, FiMinus, FiZap } from 'react-icons/fi';

const TemporaryAllocationModal = ({ show, onHide, item, so, onAllocationSaved }) => {
  const [
    // allSoItems, 
    setAllSoItems
  ] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    allocatedQuantity: ''
  });
  const [totalAllocated, setTotalAllocated] = useState(0);
  const [availableAfterAllocation, setAvailableAfterAllocation] = useState(0);
  const [actualStock, setActualStock] = useState(0);

  const fetchAllAllocations = useCallback(async () => {
    setLoading(true);
    try {
      // Get all SO items for this product (from all sales orders)
      const response = await axios.get(
        `${config.API_BASE_URL}/sales-order-items`,
        {
          params: {
            product: item.product._id,
            populate: 'salesOrder'
          }
        }
      );

      console.log('📦 Fetched all SO items for product:', response.data);
      console.log('🔍 Looking for current item._id:', item._id);

      // Filter out cancelled items
      const activeItems = response.data.filter(i =>
        i.salesOrder && i.salesOrder.status !== 'Cancelled'
      );

      setAllSoItems(activeItems);

      // Calculate total allocated across ALL orders for this product
      const total = activeItems.reduce((sum, soItem) => sum + (soItem.allocatedQty || 0), 0);
      console.log('💰 Total allocated across all orders:', total);
      setTotalAllocated(total);

      // Find THIS specific item to prefill the form
      const currentItem = activeItems.find(i => i._id?.toString() === item._id?.toString());

      console.log('✅ Found current item:', currentItem);

      if (currentItem && currentItem.allocatedQty > 0) {
        console.log('📝 Prefilling form with allocated qty:', currentItem.allocatedQty);
        setFormData({
          allocatedQuantity: currentItem.allocatedQty
        });
      } else {
        console.log('🆕 No existing allocation, clearing form');
        setFormData({
          allocatedQuantity: ''
        });
      }
    } catch (error) {
      console.error('Error fetching allocations:', error);
      toast.error('Error fetching allocations');
    } finally {
      setLoading(false);
    }
  }, [item]);

  useEffect(() => {
    if (show && item?.product?._id) {
      const stock = item?.product?.totalAvailableStock || 0;
      console.log('📊 Stock Information:', {
        productId: item.product._id,
        productName: item.product.productName,
        totalAvailableStock: item.product.totalAvailableStock,
        actualStock: stock,
        itemId: item._id,
        quantity: item.quantity,
        unit: item.unit,
        currentAllocatedQty: item.allocatedQty
      });
      setActualStock(stock);
      fetchAllAllocations();
    }
  }, [fetchAllAllocations, show, item, item?.product?._id]);

  useEffect(() => {
    setAvailableAfterAllocation(Math.max(0, actualStock - totalAllocated));
  }, [totalAllocated, actualStock]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveAllocation = async () => {
    const newQty = parseFloat(formData.allocatedQuantity);

    if (isNaN(newQty) || newQty < 0) {
      toast.error('Please enter a valid quantity (0 or greater)');
      return;
    }

    if (newQty > item.quantity) {
      toast.error(`Cannot allocate more than ordered quantity (${item.quantity} ${item.unit})`);
      return;
    }

    setLoading(true);
    try {
      // Use the unified allocation API endpoint
      await axios.put(
        `${config.API_BASE_URL}/allocation/items/${item._id}/allocate`,
        { allocatedQty: newQty }
      );

      toast.success('Allocation saved successfully');
      fetchAllAllocations();

      if (onAllocationSaved) {
        onAllocationSaved();
      }
    } catch (error) {
      console.error('Error saving allocation:', error);
      toast.error(error.response?.data?.message || 'Error saving allocation');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoAllocate = async () => {
    if (!window.confirm('Auto-allocate available stock to this item based on what\'s available?')) {
      return;
    }

    setLoading(true);
    try {
      const currentlyAllocated = item.allocatedQty || 0;
      const remaining = item.quantity - currentlyAllocated;
      const canAllocate = Math.min(remaining, availableAfterAllocation);

      if (canAllocate <= 0) {
        toast.warning('No stock available to allocate');
        return;
      }

      const newTotal = currentlyAllocated + canAllocate;

      await axios.put(
        `${config.API_BASE_URL}/allocation/items/${item._id}/allocate`,
        { allocatedQty: newTotal }
      );

      toast.success(`Auto-allocated ${canAllocate} ${item.unit}`);
      fetchAllAllocations();

      if (onAllocationSaved) {
        onAllocationSaved();
      }
    } catch (error) {
      console.error('Error auto-allocating:', error);
      toast.error(error.response?.data?.message || 'Error auto-allocating');
    } finally {
      setLoading(false);
    }
  };

  const handleClearAllocation = async () => {
    if (!window.confirm('Clear allocation for this item?')) {
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${config.API_BASE_URL}/allocation/items/${item._id}/allocate`,
        { allocatedQty: 0 }
      );

      toast.success('Allocation cleared');
      fetchAllAllocations();

      if (onAllocationSaved) {
        onAllocationSaved();
      }
    } catch (error) {
      console.error('Error clearing allocation:', error);
      toast.error('Error clearing allocation');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAdjust = (amount) => {
    const currentQty = parseFloat(formData.allocatedQuantity) || 0;
    const newQty = Math.max(0, Math.min(item.quantity, currentQty + amount));
    setFormData(prev => ({
      ...prev,
      allocatedQuantity: newQty
    }));
  };

  if (!item?.product?._id) {
    return null;
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FiSave className="me-2" />
          Stock Allocation - {item.product?.productName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <h6>Stock Information</h6>
          <div className="d-flex flex-wrap gap-3">
            <Badge bg="primary" className="py-2 px-3">
              <strong>Physical Stock:</strong> {actualStock} {item.unit}
              {/* <strong>Actual Stock:</strong> {actualStock} {item.unit} */}
            </Badge>
            <Badge bg="warning" className="py-2 px-3 text-dark">
              <strong>Total Allocated:</strong> {totalAllocated} {item.unit}
            </Badge>
            <Badge bg="success" className="py-2 px-3">
              <strong>Available for Allocation:</strong> {availableAfterAllocation} {item.unit}
            </Badge>
          </div>
          <div className="mt-2">
            <small className="text-muted">
              Total Allocated includes allocations across all sales orders for this product
            </small>
          </div>
        </div>

        <div className="mb-0">
          {/* <h6>Allocation for This Item</h6> */}
          <div className="alert alert-light text-secondary py-2 mb-4">
            <small>
              <strong>Order:</strong> {so?.orderNumber || 'N/A'} |
              <strong> Ordered:</strong> {item.quantity} {item.unit} |
              <strong> Currently Allocated:</strong> {item.allocatedQty || 0} {item.unit}
            </small>
          </div>

          <Form>
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold text-black">Allocate Quantity</Form.Label>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleQuickAdjust(-10)}
                  disabled={loading}
                >
                  <FiMinus /> 10
                </Button>
                <div className="position-relative">
                  <Form.Control
                    type="number"
                    name="allocatedQuantity"
                    value={formData.allocatedQuantity}
                    onChange={handleInputChange}
                    min="0"
                    max={item.quantity}
                    step="0.01"
                    placeholder="Enter quantity to allocate"
                    disabled={loading}
                    style={{minWidth:'229px'}}
                  />
                  <span className="unit-suffix">{item.unit}</span>
                </div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleQuickAdjust(10)}
                  disabled={loading}
                >
                  <FiPlus />10
                </Button>
              </div>
              <Form.Text className="text-muted">
                Max: {item.quantity} {item.unit} (ordered quantity)
              </Form.Text>
            </Form.Group>
          </Form>

          <div className="d-flex gap-2 pt-2">
            <Button
              variant="success"
              size="sm"
              onClick={handleAutoAllocate}
              disabled={loading || availableAfterAllocation <= 0}
            >
              <FiZap className="me-1" />
              Auto-Allocate Available
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={handleClearAllocation}
              disabled={loading || !item.allocatedQty}
            >
              Clear This Allocation
            </Button>
          </div>
        </div>

        {/* {allSoItems.length > 1 && (
          <div className="mb-4">
            <h6>All Allocations for {item.product?.productName}</h6>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              <Table bordered hover size="sm">
                <thead className="sticky-top bg-white">
                  <tr>
                    <th>Sales Order</th>
                    <th>Customer</th>
                    <th className="text-end">Ordered</th>
                    <th className="text-end">Allocated</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {allSoItems.map((soItem) => (
                    <tr key={soItem._id} className={soItem._id === item._id ? 'table-primary' : ''}>
                      <td>
                        {soItem.salesOrder?.orderNumber || 'N/A'}
                        {soItem._id === item._id && <Badge bg="primary" className="ms-2">Current</Badge>}
                      </td>
                      <td>{soItem.salesOrder?.buyer?.business_name || 'N/A'}</td>
                      <td className="text-end">{soItem.quantity} {soItem.unit}</td>
                      <td className="text-end">
                        <Badge bg={
                          soItem.allocatedQty >= soItem.quantity ? 'success' :
                            soItem.allocatedQty > 0 ? 'warning' : 'secondary'
                        }>
                          {soItem.allocatedQty || 0} {soItem.unit}
                        </Badge>
                      </td>
                      <td>
                        <small>{soItem.itemStatus || 'Pending'}</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )} */}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div>
          {/* <small className="text-muted">
            Changes are saved to the sales order item directly
          </small> */}
        </div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveAllocation}
            disabled={loading || formData.allocatedQuantity === ''}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <FiSave className="me-1" />
                Save Allocation
              </>
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default TemporaryAllocationModal;
